/**
 * Fetcha el subset público de marca via `GET /api/public/directory`. La
 * endpoint es read-only, sin auth y CORS permisivo. Cachea la respuesta en
 * sessionStorage para que las visitas al login dentro de la misma sesión
 * no repitan el roundtrip.
 *
 * El branding hoy se configura vía env var `BRAND` (objeto JSON) y se
 * inyecta en build/runtime del worker — cambia con un re-deploy. La SPA
 * no se recompila.
 */
import { useEffect, useState } from 'react';

interface PublicBrandLite {
  name: string;
  www: string | null;
}

const CACHE_KEY = 'public_brand_cache_v1';

function readCache(): PublicBrandLite | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PublicBrandLite>;
    if (typeof parsed.name !== 'string') return null;
    return {
      name: parsed.name,
      www: typeof parsed.www === 'string' ? parsed.www : null,
    };
  } catch {
    return null;
  }
}

function writeCache(brand: PublicBrandLite): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(brand));
  } catch {
    /* sessionStorage puede estar deshabilitado; degrada silencioso */
  }
}

export function usePublicBrand(): PublicBrandLite | null {
  const [brand, setBrand] = useState<PublicBrandLite | null>(() => readCache());

  useEffect(() => {
    if (brand !== null) return; // cache hit — no refetch en este mount
    let cancelled = false;
    void fetch('/api/public/directory')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { brand?: { name?: string; www?: string | null } } | null) => {
        if (cancelled || !data?.brand?.name) return;
        const next: PublicBrandLite = {
          name: data.brand.name,
          www: data.brand.www ?? null,
        };
        setBrand(next);
        writeCache(next);
      })
      .catch(() => { /* offline / 5xx — el footer caerá a un copy genérico */ });
    return () => { cancelled = true; };
  }, [brand]);

  return brand;
}
